import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CartContext } from "../context/CartContext";
import api from "../services/api";
import logo from "../assets/logo.png";

const defaultBrands = [
  { label: "NIKE", slug: "Nike", sub: ["SB", "CỔ CAO", "CỔ THẤP"] },
  { label: "ADIDAS", slug: "Adidas", sub: ["ALPHA", "BOOST", "STANDSMITH"] },
  { label: "MLB", slug: "MLB" },
  { label: "CONVERSE", slug: "Converse" },
  { label: "BALENCIAGA", slug: "Balenciaga" },
  { label: "PUMA", slug: "Puma" },
  { label: "VANS", slug: "Vans" },
  { label: "NEW BALANCE", slug: "New Balance" },
  { label: "FILA", slug: "Fila" },
  { label: "GUCCI", slug: "Gucci" },
  { label: "LV", slug: "LV" },
  { label: "JORDAN BRAND", slug: "Jordan" },
  { label: "ASICS", slug: "ASICS" },
  { label: "RICK OWENS", slug: "Rick Owens" },
  { label: "TIMBERLAND", slug: "Timberland" },
];

/* ────────────────────────────────────────────────────────────
   Reusable Material Symbol span
──────────────────────────────────────────────────────────── */
const Icon = ({ name, className = "" }) => (
  <span
    className={`material-symbols-outlined select-none ${className}`}
    style={{
      fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24",
    }}
  >
    {name}
  </span>
);

/* ════════════════════════════════════════════════════════════
   NAVBAR
════════════════════════════════════════════════════════════ */
const Navbar = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    if (saved !== null) {
      return saved === "true";
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const [brandHover, setBrandHover] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileBrandOpen, setMobileBrandOpen] = useState(false);
  const [mobileGuideOpen, setMobileGuideOpen] = useState(false);
  const [mobilePolicyOpen, setMobilePolicyOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dynamicBrands, setDynamicBrands] = useState([]);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [favoritesCount, setFavoritesCount] = useState(0);

  const navigate = useNavigate();
  const location = useLocation();
  const { cart } = useContext(CartContext);

  const totalItems = cart.items
    .filter((i) => !i.isGift)
    .reduce((t, i) => t + i.qty, 0);

  useEffect(() => {
    const token = localStorage.getItem("token");
    setIsLoggedIn(!!token);
    if (token) {
      try {
        const u = JSON.parse(localStorage.getItem("user"));
        setUser(u);
        fetchFavorites();
      } catch (error) {
        console.error("Error parsing user from localStorage", error);
      }
    }
  }, [location.pathname]);

  const fetchFavorites = async () => {
    try {
      const res = await api.get("/favorites");
      const favs = res.data.products || [];
      const favIds = favs.map((p) => p._id);
      setFavoritesCount(favIds.length);
      localStorage.setItem("favorites", JSON.stringify(favIds));
    } catch (error) {
      console.error("Error fetching favorites in Navbar:", error);
    }
  };

  /* dark-mode toggle on <html> */
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) fetchFavorites();
    fetchDynamicBrands();
    fetchPolicies();

    // Close dropdowns on route change
    setMobileOpen(false);
    setAccountOpen(false);
    setIsSearchOpen(false);

    // Lắng nghe sự kiện cập nhật thương hiệu và chính sách từ Admin
    const handleBrandsUpdated = () => fetchDynamicBrands();
    const handlePoliciesUpdated = () => fetchPolicies();

    window.addEventListener("brands_updated", handleBrandsUpdated);
    window.addEventListener("policies_updated", handlePoliciesUpdated);

    return () => {
      window.removeEventListener("brands_updated", handleBrandsUpdated);
      window.removeEventListener("policies_updated", handlePoliciesUpdated);
    };
  }, [location.pathname]);

  /* Scroll listener for compact navbar */
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 80);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchPolicies = async () => {
    try {
      const res = await api.get("/policies");
      if (res.data) setPolicies(res.data);
    } catch (error) {
      console.error("Error fetching policies in Navbar:", error);
    }
  };

  const fetchDynamicBrands = async () => {
    try {
      const response = await api.get("/settings/brands_config");
      console.log("DEBUG: Navbar brands response raw:", response.data);

      const extractBrands = (data) => {
        if (!data) return null;
        if (Array.isArray(data)) {
          const brandsEntry = data.find(
            (item) => item && (item.Key === "brands" || item.key === "brands"),
          );
          if (
            brandsEntry &&
            Array.isArray(brandsEntry.Value || brandsEntry.value)
          ) {
            return brandsEntry.Value || brandsEntry.value;
          }
          if (data.length > 0 && typeof data[0] === "string") return data;
          if (data.length > 0) return extractBrands(data[0]);
          return null;
        }
        if (data.brands && Array.isArray(data.brands)) return data.brands;
        if (data.Brands && Array.isArray(data.Brands)) return data.Brands;
        if (data.value) return extractBrands(data.value);
        if (
          (data.Key === "brands" || data.key === "brands") &&
          Array.isArray(data.Value || data.value)
        ) {
          return data.Value || data.value;
        }
        return null;
      };

      const brandListArr = extractBrands(response.data);

      if (brandListArr && brandListArr.length > 0) {
        console.log("DEBUG: Navbar extracted brands:", brandListArr);
        const mapped = brandListArr.map((b) => {
          const name = String(b).trim();
          if (name.toUpperCase() === "NIKE") {
            return {
              label: "NIKE",
              slug: "Nike",
              sub: ["SB", "CỔ CAO", "CỔ THẤP"],
            };
          }
          if (name.toUpperCase() === "ADIDAS") {
            return {
              label: "ADIDAS",
              slug: "Adidas",
              sub: ["ALPHA", "BOOST", "STANDSMITH"],
            };
          }
          return { label: name.toUpperCase(), slug: name };
        });
        setDynamicBrands(mapped);
      } else {
        console.log("DEBUG: Navbar no brands found, using default");
        setDynamicBrands(defaultBrands);
      }
    } catch (error) {
      console.error("DEBUG: Navbar error fetching brands:", error);
      setDynamicBrands(defaultBrands);
    }
  };

  const currentBrands =
    dynamicBrands.length > 0 ? dynamicBrands : defaultBrands;

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("userId");
    setIsLoggedIn(false);
    setUser(null);
    navigate("/login");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setMobileOpen(false);
      setIsSearchOpen(false);
    }
  };

  const isActive = (path) => {
    const currentFull = location.pathname + location.search;
    if (path.includes("?")) {
      return currentFull === path;
    }
    // For path "/products", only active if there's no ?category=SecondHand
    if (path === "/products") {
      return (
        location.pathname === "/products" &&
        !location.search.includes("category=SecondHand")
      );
    }
    return location.pathname === path;
  };

  /* ── nav link style helper ── */
  const linkCls = (path) =>
    `relative text-sm font-medium py-2 transition-colors after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full hover:text-blue-600 ${
      isActive(path)
        ? "text-blue-600 after:!w-full"
        : "text-gray-600 dark:text-slate-300"
    }`;

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-[100] bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 shadow-sm transition-all duration-300 ${scrolled ? "bg-white/95 dark:bg-slate-900/95 backdrop-blur-md" : ""}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex items-center justify-between transition-all duration-300 xl:${scrolled ? "h-14 sm:h-16" : "h-16 sm:h-20"} h-16 gap-2 sm:gap-4 xl:py-0`}
          >
            {/* Mobile Menu Toggle (Left on mobile) */}
            <button
              onClick={() => {
                setMobileOpen(!mobileOpen);
                setIsSearchOpen(false);
              }}
              className="xl:hidden p-2 -ml-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors border border-transparent active:border-blue-200"
              aria-label="Toggle menu"
            >
              <Icon
                name={mobileOpen ? "close" : "menu"}
                className="text-[26px]"
              />
            </button>

            {/* Logo */}
            <motion.div
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
              className="flex-shrink-0"
            >
              <Link
                to="/"
                className="flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
              >
                <img
                  src={logo}
                  alt="TaHa"
                  className={`transition-all duration-300 w-auto border-[1px] border-white rounded-full h-8 sm:h-10 xl:${scrolled ? "h-7 sm:h-8" : "h-10 sm:h-12"}`}
                />
                <span
                  className={`font-bold tracking-tight text-slate-800 dark:text-white transition-all duration-300 text-[15px] sm:text-lg xl:${scrolled ? "text-base sm:text-lg" : "text-lg sm:text-xl"}`}
                >
                  TaHa <span className="text-red-600">Shoes</span>
                </span>
              </Link>
            </motion.div>

            {/* Nav links integrated into Row 1 when scrolled (Desktop) */}
            {scrolled && (
              <nav className="hidden xl:block flex-1 ml-2 sm:ml-4 animate-fade-in">
                <ul className={`flex items-center ${dynamicBrands.length > 10 ? "gap-5" : "gap-8"}`}>
                  <li>
                    <motion.div whileTap={{ scale: 0.95 }}>
                      <Link to="/" className="text-xs font-bold text-gray-600 dark:text-slate-300 hover:text-blue-600 py-2 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full">
                        Trang chủ
                      </Link>
                    </motion.div>
                  </li>
                  <li>
                    <motion.div whileTap={{ scale: 0.95 }}>
                      <Link to="/products" className="text-xs font-bold text-gray-600 dark:text-slate-300 hover:text-blue-600 py-2 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full">
                        Sản phẩm
                      </Link>
                    </motion.div>
                  </li>
                  <li>
                    <motion.div whileTap={{ scale: 0.95 }}>
                      <Link to="/articles" className="text-xs font-bold text-gray-600 dark:text-slate-300 hover:text-blue-600 py-2 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full">
                        Bài viết
                      </Link>
                    </motion.div>
                  </li>

                  {/* Thương hiệu (Scrolled) */}
                  <li 
                    className="relative group"
                    onMouseEnter={() => setBrandHover(true)}
                    onMouseLeave={() => setBrandHover(false)}
                  >
                    <button
                      className={`flex items-center gap-0.5 text-xs font-bold py-2 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 ${brandHover ? "text-blue-600 after:w-full" : "text-gray-600 dark:text-slate-300 hover:text-blue-600 hover:after:w-full"}`}
                    >
                      Thương hiệu
                    </button>
                    <div className={`absolute left-0 top-full pt-1 transition-all duration-200 z-[110] ${brandHover ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"}`}>
                      <div className="w-[440px] bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-2xl rounded-lg overflow-hidden py-2 grid grid-cols-2">
                        {currentBrands.map((brand) => (
                          <Link
                            key={brand.slug}
                            to={`/products?brand=${encodeURIComponent(brand.slug)}`}
                            onClick={() => setBrandHover(false)}
                            className="block px-4 py-2 text-[11px] font-bold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors uppercase"
                          >
                            {brand.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </li>

                  {/* Hướng dẫn (Scrolled) */}
                  <li className="relative group">
                    <button className="flex items-center gap-0.5 text-xs font-bold py-2 text-gray-600 dark:text-slate-300 hover:text-blue-600 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 group-hover:after:w-full">
                      Hướng dẫn
                    </button>
                    <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[110]">
                      <div className="w-48 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-2xl rounded-lg overflow-hidden py-1">
                        <Link
                          to="/guide/size"
                          className="block px-4 py-2 text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-800 dark:text-slate-300 hover:text-blue-600"
                        >
                          CHỌN SIZE
                        </Link>
                        <Link
                          to="/guide/care"
                          className="block px-4 py-2 text-xs font-medium hover:bg-gray-50 dark:hover:bg-slate-800 dark:text-slate-300 hover:text-blue-600"
                        >
                          BẢO QUẢN
                        </Link>
                      </div>
                    </div>
                  </li>

                  {/* Chính sách (Scrolled) */}
                  <li className="relative group">
                    <button className="flex items-center gap-1 text-xs font-bold py-2 text-gray-600 dark:text-slate-300 hover:text-blue-600 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 group-hover:after:w-full">
                      Chính sách
                    </button>
                    <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-[110]">
                      <div className="w-52 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-2xl rounded-lg overflow-hidden py-1">
                        {policies.map((p) => (
                          <Link
                            key={p._id}
                            to={`/policy/${p.slug}`}
                            className="block px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors uppercase tracking-wider"
                          >
                            {p.title}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </li>

                  <li>
                    <motion.div whileTap={{ scale: 0.95 }}>
                      <Link
                        to="/products?category=SecondHand"
                        className="text-xs font-bold text-gray-600 dark:text-slate-300 hover:text-blue-600 py-2 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full"
                      >
                        SecondHand
                      </Link>
                    </motion.div>
                  </li>
                  <li className="hidden 2xl:block">
                    <motion.div whileTap={{ scale: 0.95 }}>
                      <Link to="/about" className="text-xs font-bold text-gray-600 dark:text-slate-300 hover:text-blue-600 py-2 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full">
                        Giới thiệu
                      </Link>
                    </motion.div>
                  </li>
                  <li className="hidden 2xl:block">
                    <motion.div whileTap={{ scale: 0.95 }}>
                      <Link to="/contact" className="text-xs font-bold text-gray-600 dark:text-slate-300 hover:text-blue-600 py-2 transition-colors relative after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300 hover:after:w-full">
                        Liên hệ
                      </Link>
                    </motion.div>
                  </li>
                </ul>
              </nav>
            )}

            {/* Search bar – hidden on mobile (Desktop only) */}
            <form
              onSubmit={handleSearch}
              className={`hidden xl:flex items-center my-auto relative group transition-all duration-300 ${scrolled ? "max-w-[130px] lg:max-w-[150px] flex-1" : "flex-1 max-w-xl"}`}
              style={{ alignSelf: "center" }}
            >
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon
                  name="search"
                  className="text-gray-400 group-focus-within:text-blue-600 transition-colors text-[18px]"
                />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  scrolled
                    ? "Tìm kiếm..."
                    : "Tìm kiếm dụng cụ, quần áo thể thao..."
                }
                className={`w-full pr-4 border border-gray-200 dark:border-slate-700 rounded-full bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-slate-200 placeholder-gray-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all ${scrolled ? "py-1.5 pl-9" : "py-2.5 pl-10"}`}
                style={{ lineHeight: "1.25rem" }}
              />
            </form>

            {/* Right icons (Mobile & Desktop) */}
            <div className="flex items-center gap-0.5 sm:gap-2">
              {/* Search Icon (Mobile only) */}
              <button
                onClick={() => {
                  setIsSearchOpen(!isSearchOpen);
                  setMobileOpen(false);
                }}
                className={`xl:hidden p-2 transition-colors ${isSearchOpen ? "text-blue-600" : "text-gray-500 dark:text-gray-400"}`}
              >
                <Icon
                  name={isSearchOpen ? "close" : "search"}
                  className="text-[24px]"
                />
              </button>

              {/* Wishlist */}
              <motion.div whileTap={{ scale: 0.9 }}>
                <Link
                  to="/wishlist"
                  className="relative p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors flex items-center justify-center"
                >
                  <Icon name="favorite" className="text-[24px] xl:text-[22px]" />
                  {favoritesCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1 shadow-sm border border-white dark:border-slate-900 z-10">
                      {favoritesCount > 99 ? "99+" : favoritesCount}
                    </span>
                  )}
                </Link>
              </motion.div>

              {/* Cart */}
              <motion.div whileTap={{ scale: 0.9 }}>
                <Link
                  to="/cart"
                  className="relative p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors flex items-center justify-center"
                >
                  <Icon
                    name="shopping_cart"
                    className="text-[24px] xl:text-[22px]"
                  />
                  {totalItems > 0 && (
                    <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-blue-600 rounded-full px-1 shadow-sm border border-white dark:border-slate-900 z-10">
                      {totalItems > 99 ? "99+" : totalItems}
                    </span>
                  )}
                </Link>
              </motion.div>

              {/* Account */}
              {isLoggedIn ? (
                <div className="relative group/account">
                  <button
                    onClick={() => setAccountOpen(!accountOpen)}
                    onMouseEnter={() =>
                      !window.matchMedia("(max-width: 1280px)").matches &&
                      setAccountOpen(true)
                    }
                    className="flex items-center gap-1.5 px-2 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sm font-medium outline-none"
                  >
                    {user && user.avatar ? (
                      <img
                        src={user.avatar}
                        alt="Avatar"
                        className="w-[24px] h-[24px] xl:w-[22px] xl:h-[22px] rounded-full object-cover shrink-0 ring-1 ring-gray-200 dark:ring-slate-700"
                      />
                    ) : (
                      <div className="w-[24px] h-[24px] xl:w-[22px] xl:h-[22px] rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[10px] shrink-0">
                        {user && user.name
                          ? user.name.charAt(0).toUpperCase()
                          : "U"}
                      </div>
                    )}
                    {!scrolled && (
                      <span className="hidden lg:block">Tài khoản</span>
                    )}
                  </button>

                  {/* Dropdown Backdrop for outside click detection on mobile */}
                  <AnimatePresence>
                    {accountOpen && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setAccountOpen(false)}
                        className="fixed inset-0 z-[55] lg:hidden"
                      />
                    )}
                  </AnimatePresence>

                  {/* Dropdown Menu */}
                  <div
                    className={`absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-xl rounded-xl py-2 transition-all duration-200 z-[60] origin-top-right
                      ${
                        accountOpen
                          ? "opacity-100 visible translate-y-0"
                          : "opacity-0 invisible -translate-y-2 pointer-events-none"
                      }
                    `}
                    onMouseLeave={() =>
                      !window.matchMedia("(max-width: 1280px)").matches &&
                      setAccountOpen(false)
                    }
                  >
                    <div className="px-4 py-2 border-b border-gray-50 dark:border-slate-800/50 mb-1 lg:hidden">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                        Tài khoản
                      </p>
                      <p className="text-sm font-black text-blue-600 truncate">
                        {user?.name}
                      </p>
                    </div>

                    <Link
                      to="/profile"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <Icon
                        name="person"
                        className="text-[18px] text-gray-400"
                      />
                      Hồ sơ của tôi
                    </Link>
                    <Link
                      to="/orders"
                      onClick={() => setAccountOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
                    >
                      <Icon
                        name="package_2"
                        className="text-[18px] text-gray-400"
                      />
                      Đơn hàng của tôi
                    </Link>
                    {user && (user.isAdmin || user.role === "manager") && (
                      <Link
                        to="/admin"
                        onClick={() => setAccountOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors font-bold"
                      >
                        <Icon
                          name="admin_panel_settings"
                          className="text-[18px] text-blue-600"
                        />
                        Quản trị viên
                      </Link>
                    )}
                    <div className="mx-2 my-1 border-t border-gray-100 dark:border-slate-800"></div>
                    <button
                      onClick={() => {
                        handleLogout();
                        setAccountOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                    >
                      <Icon name="logout" className="text-[18px]" />
                      Đăng xuất
                    </button>
                  </div>
                </div>
              ) : (
                <Link
                  to="/login"
                  className="flex items-center gap-1.5 px-2 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors text-sm font-medium"
                >
                  <Icon
                    name="account_circle"
                    className="text-[24px] xl:text-[22px]"
                  />
                  {!scrolled && (
                    <span className="hidden lg:block">Đăng nhập</span>
                  )}
                </Link>
              )}

              {/* Dark mode */}
              <button
                onClick={() => setDarkMode(!darkMode)}
                className="p-2 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-yellow-400 transition-all duration-300 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800"
                title={
                  darkMode
                    ? "Chuyển sang chế độ sáng"
                    : "Chuyển sang chế độ tối"
                }
              >
                {darkMode ? (
                  <Icon
                    name="light_mode"
                    className="text-[24px] xl:text-[22px] text-yellow-400 drop-shadow-[0_0_8px_rgba(250,204,21,0.4)]"
                  />
                ) : (
                  <Icon
                    name="dark_mode"
                    className="text-[24px] xl:text-[22px]"
                  />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ══ ROW 3: Nav links (desktop) ══ */}
        <div className="border-t border-gray-100 dark:border-slate-800">
          {!scrolled && (
            <nav className="hidden xl:block bg-gray-50/50 dark:bg-slate-900/50 backdrop-blur-sm">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-center h-14">
                  <ul className="flex items-center gap-10 text-sm font-medium">
                    {/* Trang chủ */}
                    <li>
                      <motion.div whileTap={{ scale: 0.95 }}>
                        <Link to="/" className={linkCls("/")}>
                          Trang chủ
                        </Link>
                      </motion.div>
                    </li>

                    {/* Sản phẩm */}
                    <li>
                      <motion.div whileTap={{ scale: 0.95 }}>
                        <Link to="/products" className={linkCls("/products")}>
                          Sản phẩm
                        </Link>
                      </motion.div>
                    </li>

                    <li>
                      <motion.div whileTap={{ scale: 0.95 }}>
                        <Link to="/articles" className={linkCls("/articles")}>
                          Bài viết
                        </Link>
                      </motion.div>
                    </li>

                    {/* Thương hiệu – hover dropdown */}
                    <li
                      className="relative"
                      onMouseEnter={() => setBrandHover(true)}
                      onMouseLeave={() => setBrandHover(false)}
                    >
                      <button
                        className={`flex items-center gap-0.5 text-sm font-medium py-2 transition-colors relative
                        after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300
                        ${brandHover ? "text-blue-600 after:!w-full" : "text-gray-600 dark:text-slate-300 hover:text-blue-600 hover:after:w-full"}`}
                      >
                        Thương hiệu
                        <Icon
                          name="expand_more"
                          className={`text-[18px] transition-transform duration-200 ${brandHover ? "rotate-180" : ""}`}
                        />
                      </button>

                      {/* Dropdown panel */}
                      <div
                        className={`absolute left-0 top-full pt-2 transition-all duration-200 z-50 ${
                          brandHover
                            ? "opacity-100 visible translate-y-0"
                            : "opacity-0 invisible -translate-y-1"
                        }`}
                      >
                        <div className="w-[480px] bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-xl rounded-b-lg overflow-visible">
                          <ul className="py-2 grid grid-cols-2">
                            {currentBrands.map((brand) =>
                              brand.sub ? (
                                /* Brand WITH sub-menu */
                                <li
                                  key={brand.slug}
                                  className="nav-sub-item relative"
                                >
                                  <Link
                                    to={`/products?brand=${encodeURIComponent(brand.slug)}`}
                                    onClick={() => setBrandHover(false)}
                                    className="flex items-center justify-between px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors"
                                  >
                                    <span>{brand.label}</span>
                                    <Icon
                                      name="chevron_right"
                                      className="text-[16px]"
                                    />
                                  </Link>

                                  {/* Sub-menu */}
                                  <ul className="nav-submenu absolute left-full top-0 w-44 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-xl rounded-r-lg py-2 opacity-0 invisible transition-all duration-200 z-[60]">
                                    {brand.sub.map((sub) => (
                                      <li key={sub}>
                                        <Link
                                          to={`/products?brand=${encodeURIComponent(brand.slug)}&sub=${encodeURIComponent(sub)}`}
                                          onClick={() => setBrandHover(false)}
                                          className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors"
                                        >
                                          {sub}
                                        </Link>
                                      </li>
                                    ))}
                                  </ul>
                                </li>
                              ) : (
                                /* Brand WITHOUT sub-menu */
                                <li key={brand.slug}>
                                  <Link
                                    to={`/products?brand=${encodeURIComponent(brand.slug)}`}
                                    onClick={() => setBrandHover(false)}
                                    className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors"
                                  >
                                    {brand.label}
                                  </Link>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      </div>
                    </li>

                    {/* Hướng dẫn – hover dropdown */}
                    <li className="relative group/guide">
                      <button
                        className={`flex items-center gap-0.5 text-sm font-medium py-2 transition-colors relative
          after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300
          group-hover/guide:text-blue-600 group-hover/guide:after:!w-full
          text-gray-600 dark:text-slate-300`}
                      >
                        Hướng dẫn
                        <Icon
                          name="expand_more"
                          className="text-[18px] transition-transform duration-200 group-hover/guide:rotate-180"
                        />
                      </button>

                      <div className="absolute left-0 top-full pt-2 z-50 opacity-0 invisible -translate-y-1 group-hover/guide:opacity-100 group-hover/guide:visible group-hover/guide:translate-y-0 transition-all duration-200">
                        <div className="w-64 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-xl rounded-b-lg overflow-hidden">
                          <ul className="py-2">
                            <li>
                              <Link
                                to="/guide/size"
                                className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors"
                              >
                                HƯỚNG DẪN CHỌN SIZE
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="/guide/care"
                                className="block px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors"
                              >
                                HƯỚNG DẪN SỬ DỤNG - BẢO QUẢN
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>

                    {/* Chính sách – hover dropdown */}
                    <li className="relative group/policy">
                      <button
                        className={`flex items-center gap-1.5 text-sm font-medium py-2 transition-colors relative
          after:absolute after:w-0 after:h-[2px] after:bottom-[-4px] after:left-0 after:bg-blue-600 after:transition-all after:duration-300
          group-hover/policy:text-blue-600 group-hover/policy:after:!w-full
          text-gray-600 dark:text-slate-300`}
                      >
                        <span className="mr-0.5">Chính sách</span>
                        <Icon
                          name="expand_more"
                          className="text-[18px] transition-transform duration-200 group-hover/policy:rotate-180"
                        />
                      </button>

                      <div className="absolute left-0 top-full pt-2 z-50 opacity-0 invisible -translate-y-1 group-hover/policy:opacity-100 group-hover/policy:visible group-hover/policy:translate-y-0 transition-all duration-200">
                        <div className="w-64 bg-white dark:bg-slate-900 border border-gray-100 dark:border-slate-800 shadow-xl rounded-b-lg overflow-hidden">
                          <ul className="py-2">
                            {policies.map((p) => (
                              <li key={p._id}>
                                <Link
                                  to={`/policy/${p.slug}`}
                                  className="block px-4 py-2.5 text-sm font-bold text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-blue-600 transition-colors uppercase tracking-tight"
                                >
                                  {p.title}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </li>

                    {/* Sản phẩm SecondHand */}
                    <li>
                      <motion.div whileTap={{ scale: 0.95 }}>
                        <Link
                          to="/products?category=SecondHand"
                          className={linkCls("/products?category=SecondHand")}
                        >
                          SecondHand
                        </Link>
                      </motion.div>
                    </li>

                    {/* Giới thiệu */}
                    <li>
                      <motion.div whileTap={{ scale: 0.95 }}>
                        <Link to="/about" className={linkCls("/about")}>
                          Giới thiệu
                        </Link>
                      </motion.div>
                    </li>

                    {/* Liên hệ */}
                    <li>
                      <motion.div whileTap={{ scale: 0.95 }}>
                        <Link to="/contact" className={linkCls("/contact")}>
                          Liên hệ
                        </Link>
                      </motion.div>
                    </li>
                  </ul>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* ══ Mobile Search Bar (Directly below navbar) ══ */}
      <AnimatePresence>
        {isSearchOpen && (
          <>
            {/* Overlay for outside click */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSearchOpen(false)}
              className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-[2px] z-[90] xl:hidden"
            />
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -20, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed top-16 left-0 right-0 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 p-4 z-[91] xl:hidden"
            >
              <form onSubmit={handleSearch} className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <Icon name="search" className="text-blue-600 text-[18px]" />
                </div>
                <input
                  type="text"
                  autoFocus
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nhập tên sản phẩm cần tìm..."
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-slate-800 border border-blue-100 dark:border-slate-700 rounded-2xl text-sm outline-none ring-2 ring-transparent focus:ring-blue-600/20 transition-all font-bold text-gray-900 dark:text-white"
                />
              </form>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ══ Mobile menu ══ */}
      <AnimatePresence>
        {mobileOpen && (
          <div className="xl:hidden">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-[6px] z-[1000]"
            />

            <motion.div
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -30, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="fixed left-0 right-0 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-slate-800 shadow-xl overflow-y-auto z-[1001] top-16 max-h-[calc(100vh-64px)]"
            >
              <ul className="flex flex-col py-2 px-4 gap-0.5">
                <li>
                  <motion.div whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/"
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600 border-b border-gray-50 dark:border-slate-800"
                    >
                      Trang Chủ
                    </Link>
                  </motion.div>
                </li>
                <li>
                  <motion.div whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/products"
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600 border-b border-gray-50 dark:border-slate-800"
                    >
                      Sản Phẩm
                    </Link>
                  </motion.div>
                </li>

                {/* Mobile brand accordion */}
                <li className="border-b border-gray-50 dark:border-slate-800">
                  <button
                    onClick={() => setMobileBrandOpen(!mobileBrandOpen)}
                    className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600"
                  >
                    <span>Thương Hiệu</span>
                    <Icon
                      name={mobileBrandOpen ? "expand_less" : "expand_more"}
                      className="text-[18px]"
                    />
                  </button>
                  {mobileBrandOpen && (
                    <ul className="pl-4 pb-2 flex flex-col gap-0.5">
                      {currentBrands.map((brand) => (
                        <li key={brand.slug}>
                          <Link
                            to={`/products?brand=${encodeURIComponent(brand.slug)}`}
                            onClick={() => {
                              setMobileOpen(false);
                              setMobileBrandOpen(false);
                            }}
                            className="block py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-blue-600 uppercase"
                          >
                            {brand.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                {/* Mobile Guide accordion */}
                <li className="border-b border-gray-50 dark:border-slate-800">
                  <button
                    onClick={() => setMobileGuideOpen(!mobileGuideOpen)}
                    className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600"
                  >
                    <span>Hướng Dẫn</span>
                    <Icon
                      name={mobileGuideOpen ? "expand_less" : "expand_more"}
                      className="text-[18px]"
                    />
                  </button>
                  {mobileGuideOpen && (
                    <ul className="pl-4 pb-2 flex flex-col gap-0.5">
                      <li>
                        <Link
                          to="/guide/size"
                          onClick={() => setMobileOpen(false)}
                          className="block py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-blue-600 transition-colors"
                        >
                          Hướng Dẫn Chọn Size
                        </Link>
                      </li>
                      <li>
                        <Link
                          to="/guide/care"
                          onClick={() => setMobileOpen(false)}
                          className="block py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-blue-600 transition-colors"
                        >
                          Hướng Dẫn Cách Bảo Quản
                        </Link>
                      </li>
                    </ul>
                  )}
                </li>

                {/* Mobile Policy accordion */}
                <li className="border-b border-gray-50 dark:border-slate-800">
                  <button
                    onClick={() => setMobilePolicyOpen(!mobilePolicyOpen)}
                    className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600"
                  >
                    <span>Chính Sách</span>
                    <Icon
                      name={mobilePolicyOpen ? "expand_less" : "expand_more"}
                      className="text-[18px]"
                    />
                  </button>
                  {mobilePolicyOpen && (
                    <ul className="pl-4 pb-2 flex flex-col gap-0.5 underline-offset-4">
                      {policies.map((p) => (
                        <li key={p._id}>
                          <Link
                            to={`/policy/${p.slug}`}
                            onClick={() => setMobileOpen(false)}
                            className="block py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-blue-600 transition-colors capitalize"
                          >
                            {p.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>

                {/* Mobile SecondHand Link */}
                <li className="border-b border-gray-50 dark:border-slate-800">
                  <Link
                    to="/articles"
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-blue-600"
                  >
                    Bài viết
                  </Link>
                </li>

                {/* Mobile SecondHand Link */}
                <li className="border-b border-gray-50 dark:border-slate-800">
                  <Link
                    to="/products?category=SecondHand"
                    onClick={() => setMobileOpen(false)}
                    className="block py-3 text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-blue-600"
                  >
                    SecondHand
                  </Link>
                </li>

                <li>
                  <motion.div whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/about"
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600 border-b border-gray-50 dark:border-slate-800"
                    >
                      Giới Thiệu
                    </Link>
                  </motion.div>
                </li>
                <li>
                  <motion.div whileTap={{ scale: 0.98 }}>
                    <Link
                      to="/contact"
                      onClick={() => setMobileOpen(false)}
                      className="block py-3 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600 border-b border-gray-50 dark:border-slate-800"
                    >
                      Liên Hệ
                    </Link>
                  </motion.div>
                </li>
                <li className="pt-2 flex items-center justify-between border-t border-gray-50 dark:border-slate-800 mt-2">
                  {isLoggedIn ? (
                    <div className="flex gap-4">
                      <Link
                        to="/profile"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 py-2 text-sm font-medium text-blue-600"
                      >
                        <Icon name="account_circle" className="text-[20px]" />
                        Hồ Sơ
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileOpen(false);
                        }}
                        className="flex items-center gap-2 py-2 text-sm font-medium text-red-500"
                      >
                        <Icon name="logout" className="text-[20px]" />
                        Đăng Xuất
                      </button>
                    </div>
                  ) : (
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:text-blue-600"
                    >
                      <Icon name="account_circle" className="text-[20px]" />
                      Tài Khoản / Đăng Nhập
                    </Link>
                  )}

                  {/* Dark mode toggle in mobile menu */}
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className="p-2.5 rounded-full bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-300 transition-all active:scale-90"
                  >
                    {darkMode ? (
                      <Icon
                        name="light_mode"
                        className="text-[20px] text-yellow-400"
                      />
                    ) : (
                      <Icon
                        name="dark_mode"
                        className="text-[20px] text-blue-500"
                      />
                    )}
                  </button>
                </li>
              </ul>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
